<template>
  <aside class="style-bulk-edit-drawer">
    <header class="style-bulk-edit-drawer__header">
      <div>
        <h2 class="style-bulk-edit-drawer__title">
          Edit styles in bulk
        </h2>

        <span class="style-bulk-edit-drawer__selection-count">
          {{ selectedCount }}
          {{ selectedCount === 1 ? 'style selected' : 'styles selected' }}
        </span>
      </div>

      <button
        type="button"
        class="style-bulk-edit-drawer__close"
        aria-label="Close bulk update drawer"
        @click="$emit('close')"
      >
        <i class="el-icon-close" />
      </button>
    </header>

    <div class="style-bulk-edit-drawer__team-toggle">
      <ElementCheckbox
        :value="applyForAllFilteredStyles"
        @change="onApplyForAllFilteredStylesChange"
      >
        Apply to all filtered styles
      </ElementCheckbox>

      <div
        v-if="applyForAllFilteredStyles"
        class="style-bulk-edit-drawer__team-note"
      >
        This will update every style matching the current filters.
      </div>
    </div>

    <div class="style-bulk-edit-drawer__info">
      <div class="style-bulk-edit-drawer__info-icon">
        <i class="el-icon-info" />
      </div>

      <div class="style-bulk-edit-drawer__info-content">
        <div class="style-bulk-edit-drawer__info-text">
          <template v-if="changedFieldsCount === 0">
            No changes yet. Only modified fields will be updated.
          </template>

          <template v-else>
            {{ changedFieldsCount }}
            {{ changedFieldsCount === 1 ? 'field has' : 'fields have' }}
            been modified.
          </template>
        </div>

        <button
          type="button"
          class="style-bulk-edit-drawer__clear-all"
          @click="resetChanges"
        >
          Clear all
        </button>
      </div>
    </div>

    <div class="style-bulk-edit-drawer__body">
      <section
        v-for="field in visibleFields"
        :key="field.id"
        :class="[
          'style-bulk-edit-drawer__field',
          {
            'style-bulk-edit-drawer__field--changed': isFieldChanged(field.id)
          }
        ]"
      >
        <div class="style-bulk-edit-drawer__field-header">
          <label
            class="style-bulk-edit-drawer__label"
            :for="getFieldInputId(field.id)"
          >
            {{ field.label }}
          </label>

          <span
            v-if="isFieldChanged(field.id)"
            class="style-bulk-edit-drawer__modified"
          >
            Modified
          </span>
        </div>

        <ElementTooltip
          v-if="getFieldDisabledReason(field)"
          :content="getFieldDisabledReason(field)"
          placement="top"
          effect="dark"
          :open-delay="250"
        >
          <div class="style-bulk-edit-drawer__control-wrap">
            <ElementDatePicker
              v-if="field.type === 'date'"
              :id="getFieldInputId(field.id)"
              v-model="changes[field.id]"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="field.placeholder"
              class="style-bulk-edit-drawer__control"
              :disabled="true"
            />

            <ElementSelect
              v-else
              :id="getFieldInputId(field.id)"
              v-model="changes[field.id]"
              :placeholder="getFieldPlaceholder(field)"
              :disabled="true"
              clearable
              filterable
              class="style-bulk-edit-drawer__control"
            >
              <ElementOption
                v-for="option in getFieldOptions(field)"
                :key="getOptionKey(field, option)"
                :label="getOptionLabel(option)"
                :value="getOptionValue(option)"
              />
            </ElementSelect>
          </div>
        </ElementTooltip>

        <template v-else>
          <ElementDatePicker
            v-if="field.type === 'date'"
            :id="getFieldInputId(field.id)"
            v-model="changes[field.id]"
            type="date"
            value-format="yyyy-MM-dd"
            :placeholder="field.placeholder"
            class="style-bulk-edit-drawer__control"
            @change="markFieldChanged(field.id)"
          />

          <ElementSelect
            v-else
            :id="getFieldInputId(field.id)"
            v-model="changes[field.id]"
            :placeholder="getFieldPlaceholder(field)"
            :disabled="isFieldDisabled(field)"
            clearable
            filterable
            class="style-bulk-edit-drawer__control"
            @change="markFieldChanged(field.id)"
          >
            <ElementOption
              v-for="option in getFieldOptions(field)"
              :key="getOptionKey(field, option)"
              :label="getOptionLabel(option)"
              :value="getOptionValue(option)"
            />
          </ElementSelect>
        </template>
      </section>
    </div>

    <footer class="style-bulk-edit-drawer__footer">
      <ElementButton
        plain
        class="style-bulk-edit-drawer__reset"
        :disabled="!hasChanges"
        @click="resetChanges"
      >
        Reset
      </ElementButton>

      <ElementButton
        type="primary"
        :disabled="!canApply"
        @click="applyChanges"
      >
        {{ applyButtonLabel }}
      </ElementButton>
    </footer>
  </aside>
</template>

<script>
const BULK_EDIT_FIELDS = [
  {
    id: 'responsibleProductId',
    filterKey: 'responsible_product_id',
    payloadKey: 'responsible_product_id',
    label: 'Responsible Product',
    type: 'select',
    placeholder: 'Select responsible product',
    disableForAllTeam: true
  },
  {
    id: 'digitalApproverId',
    filterKey: 'digital_approver_id',
    payloadKey: 'digital_approver_id',
    label: 'Digital Approver',
    type: 'select',
    placeholder: 'Select digital approver',
    disableForAllTeam: true
  },
  {
    id: 'vendorCompanyId',
    filterKey: 'vendor_company_id',
    payloadKey: 'assigned_vendor_company_id',
    label: 'Assigned Vendor Company',
    type: 'select',
    placeholder: 'Select vendor company',
    disableForAllTeam: true
  },
  {
    id: 'submissionDeadlineDate',
    filterKey: 'submission_deadline_after',
    payloadKey: 'submission_deadline',
    label: 'Submission deadline',
    type: 'date',
    placeholder: 'Select submission deadline'
  },
  {
    id: 'qcPassed',
    filterKey: 'qc_passed',
    payloadKey: 'qc_passed',
    label: 'QC status',
    type: 'select',
    placeholder: 'Select QC status'
  },
  {
    id: 'gender',
    filterKey: 'Gender',
    payloadKey: 'gender',
    label: 'Gender',
    type: 'select',
    placeholder: 'Select gender',
    disableForAllTeam: true
  },
  {
    id: 'productGroup',
    filterKey: 'Product Group',
    payloadKey: 'product_group',
    label: 'Product Group',
    type: 'select',
    placeholder: 'Select product group',
    disableForAllTeam: true
  }
]

export default {
  name: 'StyleBulkEditDrawer',

  props: {
    selectedStyleIds: {
      type: Array,
      default: () => []
    },

    availableFilters: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      changedFields: {},
      changes: this.createInitialChanges(),
      applyForAllFilteredStyles: false
    }
  },

  computed: {
    /**
     * @returns {boolean}
     */
    isAllTeam () {
      return this.$route?.query?.group_id === 'all'
    },

    /**
     * @returns {number}
     */
    changedFieldsCount () {
      return Object.keys(this.changedFields).filter(
        key => this.changedFields[key]
      ).length
    },

    /**
     * @returns {number}
     */
    selectedCount () {
      return this.selectedStyleIds.length
    },

    /**
     * @returns {boolean}
     */
    hasChanges () {
      return Object.values(this.changedFields).some(Boolean)
    },

    /**
     * Only render fields whose filter data exists.
     *
     * Date fields are still rendered when their filter contains no options.
     *
     * @returns {object[]}
     */
    visibleFields () {
      return BULK_EDIT_FIELDS
    },

    /**
     * @returns {boolean}
     */
    canApply () {
      return (
        this.hasChanges &&
        (this.selectedCount > 0 || this.applyForAllFilteredStyles)
      )
    },

    /**
     * @returns {string}
     */
    applyButtonLabel () {
      return this.applyForAllFilteredStyles
        ? 'Apply to styles'
        : `Apply to ${this.selectedCount}`
    }
  },

  methods: {
    /**
     * @returns {object}
     */
    createInitialChanges () {
      return BULK_EDIT_FIELDS.reduce((changes, field) => {
        changes[field.id] = null

        return changes
      }, {})
    },

    /**
     * @param   {string}  fieldId
     *
     * @returns {boolean}
     */
    isFieldChanged (fieldId) {
      return Boolean(this.changedFields[fieldId])
    },

    /**
     * @param   {object}  field
     *
     * @returns {boolean}
     */
    isFieldDisabled (field) {
      return (
        (field.disableForAllTeam && this.isAllTeam) ||
        (field.type !== 'date' && this.getFieldOptions(field).length === 0)
      )
    },

    /**
     * @param   {object} field
     *
     * @returns {string}
     */
    getFieldDisabledReason (field) {
      if (field.disableForAllTeam && this.isAllTeam) {
        return 'This field is not available when editing team “All”.'
      }

      if (field.type !== 'date' && this.getFieldOptions(field).length === 0) {
        return `No ${field.label.toLowerCase()} options available`
      }

      return ''
    },

    /**
     * @param {string} fieldId
     */
    markFieldChanged (fieldId) {
      this.$set(this.changedFields, fieldId, true)
    },

    /**
     * @param   {object}   field
     *
     * @returns {object[]}
     */
    getFieldOptions (field) {
      return this.availableFilters[field.filterKey] || []
    },

    /**
     * @param   {object} field
     *
     * @returns {string}
     */
    getFieldPlaceholder (field) {
      if (field.disableForAllTeam && this.isAllTeam) {
        return 'Not available when editing all teams'
      }

      if (this.getFieldOptions(field).length === 0) {
        return `No ${field.label.toLowerCase()} options available`
      }

      return field.placeholder
    },

    /**
     * @param   {object}                     option
     *
     * @returns {string|number|boolean|null}
     */
    getOptionValue (option) {
      if (Object.prototype.hasOwnProperty.call(option, 'id')) {
        return option.id
      }

      if (Object.prototype.hasOwnProperty.call(option, 'value')) {
        return option.value
      }

      return null
    },

    /**
     * @param   {object} option
     *
     * @returns {string}
     */
    getOptionLabel (option) {
      if (
        option.label !== undefined &&
        option.label !== null &&
        option.label !== ''
      ) {
        return String(option.label)
      }

      if (
        option.name !== undefined &&
        option.name !== null &&
        option.name !== ''
      ) {
        return String(option.name)
      }

      if (option.value !== undefined && option.value !== null) {
        return String(option.value)
      }

      if (option.id !== undefined && option.id !== null) {
        return String(option.id)
      }

      return ''
    },

    /**
     * @param   {object} field
     * @param   {object} option
     *
     * @returns {string}
     */
    getOptionKey (field, option) {
      return `${field.id}-${String(this.getOptionValue(option))}`
    },

    /**
     * @param   {string} fieldId
     *
     * @returns {string}
     */
    getFieldInputId (fieldId) {
      return `style-bulk-edit-${fieldId}`
    },

    /**
     * @param {boolean} value
     */
    onApplyForAllFilteredStylesChange (value) {
      this.applyForAllFilteredStyles = value

      this.$emit('bulk-scope-change', value)
    },

    /**
     * Reset all changes and mode-specific state.
     */
    resetChanges () {
      this.changedFields = {}
      this.changes = this.createInitialChanges()
      this.applyForAllFilteredStyles = false

      this.$emit('bulk-scope-change', false)
    },

    /**
     * Emit the bulk update request payload.
     */
    applyChanges () {
      if (!this.canApply) {
        return
      }

      const requestPayload = Object.keys(this.changedFields).reduce(
        (payload, fieldId) => {
          if (!this.changedFields[fieldId]) {
            return payload
          }

          const field = BULK_EDIT_FIELDS.find(item => item.id === fieldId)

          payload[field.payloadKey] = this.changes[fieldId]

          return payload
        },
        {}
      )

      if (this.applyForAllFilteredStyles) {
        requestPayload.apply_for_all_filtered_styles = true
      } else {
        requestPayload.style_ids = [...this.selectedStyleIds]
      }

      this.$emit('apply', requestPayload)
    }
  }
}
</script>

<style lang="scss" scoped>
$drawer-width: spacing(34);

.style-bulk-edit-drawer {
  display: flex;
  flex: 0 0 $drawer-width;
  flex-direction: column;
  width: $drawer-width;
  min-width: $drawer-width;
  height: 100%;
  overflow: hidden;
  background: $white;
  border-right: 1px solid rgba($blue, 0.25);
}

.style-bulk-edit-drawer__header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  padding: spacing(2);
}

.style-bulk-edit-drawer__info {
  display: flex;
  gap: spacing(1.5);
  align-items: flex-start;
  margin: spacing(2);
  padding: spacing(1.5);
  background: #f8fbff;
  border: 1px solid #dbeafe;
  border-radius: spacing(1);
}

.style-bulk-edit-drawer__info-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: spacing(2.5);
  height: spacing(2.5);
  color: $blue;
  font-size: spacing(2);
}

.style-bulk-edit-drawer__info-content {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.style-bulk-edit-drawer__info-text {
  margin-bottom: spacing(0.5);
  color: $grey-dark;
  font-size: $font-size-label;
  line-height: 1.4;
}

.style-bulk-edit-drawer__clear-all {
  align-self: flex-start;
  padding: 0;
  color: $blue;
  font-weight: $font-weight-semibold;
  font-size: $font-size-label;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.style-bulk-edit-drawer__title {
  margin: 0;
  color: $grey-dark;
  font-size: $font-size-body;
  text-transform: uppercase;
}

.style-bulk-edit-drawer__selection-count {
  display: block;
  margin-top: spacing(0.5);
  color: $grey;
  font-size: $font-size-label;
}

.style-bulk-edit-drawer__close {
  padding: spacing(0.5);
  color: $grey;
  font-size: spacing(2);
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: $blue;
  }
}

.style-bulk-edit-drawer__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: spacing(2);
  overflow-y: auto;
}

.style-bulk-edit-drawer__description {
  margin: 0 0 spacing(2);
  color: $grey;
  font-size: $font-size-label;
  line-height: 1.4;
}

.style-bulk-edit-drawer__field {
  margin-bottom: spacing(1);
  padding: spacing(1.25);
  background: $white;
  border: 1px solid transparent;
  border-radius: $border-radius-s;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.style-bulk-edit-drawer__field--changed {
  background: rgba($blue, 0.035);
  border-color: rgba($blue, 0.2);
}

.style-bulk-edit-drawer__field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: spacing(1);
}

.style-bulk-edit-drawer__label {
  color: $grey-dark;
  font-weight: $font-weight-semibold;
  font-size: $font-size-label-medium;
}

.style-bulk-edit-drawer__modified {
  color: $blue;
  font-size: $font-size-label;
}

.style-bulk-edit-drawer__control {
  width: 100%;
}

.style-bulk-edit-drawer__control-wrap {
  width: 100%;
}

.style-bulk-edit-drawer__footer {
  display: flex;
  flex-shrink: 0;
  gap: spacing(1);
  justify-content: flex-end;
  padding: spacing(2);
  padding-top: spacing(1.5);
  background: $white;
  border-top: 1px solid rgba($blue, 0.2);
}

.style-bulk-edit-drawer__reset {
  margin-right: auto;
}

.style-bulk-edit-drawer__team-toggle {
  margin: 0 spacing(2);
  padding: 0 0 spacing(1.5);
}

.style-bulk-edit-drawer__team-note {
  margin-top: spacing(0.75);
  margin-left: spacing(3);
  color: $grey;
  font-size: $font-size-label;
  line-height: 1.4;
}
</style>
